/**
 * „Tak zobaczysz serię w planie: Ciężar · powt. · RPE" — the set table's own head, live.
 *
 * ── The line is the APP's artefact, not a label list ────────────────────────────────────────
 * `exerciseLibrary.createWizard.category.preview.strength` reads `CIĘŻAR · POWT. · RPE` in the
 * phone's own locale. That is evidence about the vocabulary rather than a string to print: the
 * trainer sees CIĘŻAR where `exerciseLibrary.field.weight` says `KG`, POWT. where the field group
 * says `Powt.`, and RPE where both agree. So three of the nine are spelled OUT — weight, distance
 * and speed — and read their word from the measurement dictionary's full title
 * (`…category.measurement.weight.title` → „Ciężar"); the other six read the set table's own
 * abbreviation (`exerciseLibrary.field.*`). That is exactly what the specimen's `kpPreview` does.
 *
 * The line is printed in SENTENCE case (polish of 4 November 2026, where it used to be capitals):
 * `metricsPreviewLine` takes the resolved words, so the casing rule is tested here rather than
 * living in a component.
 *
 * ── Keys, and the order is the dictionary's ─────────────────────────────────────────────────
 * `model/` may not translate. And the order is `sortMeasurementFields`' — the ONE order in the app
 * (measurements R4), so the line and the set table it previews cannot read differently.
 */

import { sortMeasurementFields, type MeasurementFieldKey } from '~/shared/measurement';

/** One word in the line, as the key that spells it. */
export interface MetricPreviewPart {
  readonly field: MeasurementFieldKey;
  readonly labelKey: string;
}

/** The three the app spells out rather than abbreviating. See the header. */
const SPELLED_OUT: readonly MeasurementFieldKey[] = ['weight', 'distance', 'speed'];

/** The preview line's words, in the dictionary's order. EMPTY when nothing is measured. */
export function metricsPreview(
  fields: readonly MeasurementFieldKey[],
): readonly MetricPreviewPart[] {
  return sortMeasurementFields(fields).map((field) => ({
    field,
    labelKey: SPELLED_OUT.includes(field)
      ? `exerciseLibrary.createWizard.category.measurement.${field}.title`
      : `exerciseLibrary.field.${field}`,
  }));
}

/** The title and the description a metric ROW prints, as keys. The phone's own full strings. */
export function metricRowKeys(field: MeasurementFieldKey): {
  readonly titleKey: string;
  readonly descriptionKey: string;
} {
  return {
    titleKey: `exerciseLibrary.createWizard.category.measurement.${field}.title`,
    descriptionKey: `exerciseLibrary.createWizard.category.measurement.${field}.description`,
  };
}

/**
 * The resolved words as one sentence-case line: the first word as the dictionary spells it, every
 * later one in lower case — except an abbreviation written in capitals (RPE, RIR, KG), which stays
 * as it is. „Ciężar · powt. · RPE", never „Ciężar · Powt. · Rpe".
 */
export function metricsPreviewLine(words: readonly string[], locale: string): string {
  return words
    .map((word, index) =>
      index === 0 || word === word.toLocaleUpperCase(locale) ? word : word.toLocaleLowerCase(locale),
    )
    .join(' · ');
}
